import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(", ");
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bp-card rounded-2xl p-8">
        <div className="overline mb-2">Welcome back</div>
        <h1 className="font-heading text-3xl text-white font-light tracking-tight mb-6">
          Log in to your vault
        </h1>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-gray-300">Email</Label>
            <Input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 bg-[#050914] border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <Label className="text-gray-300">Password</Label>
            <Input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 bg-[#050914] border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
            />
          </div>
          <Button
            type="submit"
            data-testid="login-submit"
            disabled={busy}
            className="w-full btn-gold rounded-full h-11"
          >
            {busy ? "Signing in..." : "Sign in"}
          </Button>
        </form>
        <div className="text-center text-sm text-gray-400 mt-6">
          No account?{" "}
          <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="link-register">
            Create one
          </Link>
        </div>
        <div className="text-center text-xs text-gray-500 mt-2">
          <Link to="/forgot-password" className="hover:text-[#D4AF37]" data-testid="link-forgot">
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
}
